<script>
import moment from 'moment';
import { Scatter } from 'vue-chartjs';

export default {
  extends: Scatter,
  props: {
    data: {
      type: Object,
      default: null,
    },
    isCurrency: {
      type: Boolean,
      default: true,
    },
    itemValueDecorator: {
      type: Function,
      default: null,
    },
    multiplier: {
      type: Number,
      default: 1,
    },
    isRetentionRate: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      datasets: [],
      xAxes: [],
      currentDatasetConfig: {},
      compareDatasetConfig: {},
      currentXAxisConfig: {},
      compareXAxisConfig: {},
      currentLineColor: '#743B91',
      compareLineColor: '#51A5D3',
    };
  },
  watch: {
    data: function () {
      this.renderLineChart();
    },
  },
  created() {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const currentGradient = ctx.createLinearGradient(0, 0, 0, 300);
    currentGradient.addColorStop(0, this.currentLineColor);
    currentGradient.addColorStop(1, '#fff');

    const compareGradient = ctx.createLinearGradient(0, 50, 0, 280);
    compareGradient.addColorStop(0, this.compareLineColor);
    compareGradient.addColorStop(1, '#fff');

    this.currentDatasetConfig = {
      label: 'Current',
      xAxisID: 'x-axis-1',
      borderColor: this.currentLineColor,
      backgroundColor: currentGradient,
      pointHoverBackgroundColor: '#fff',
      showLine: true,
      borderWidth: 2,
      lineTension: 0.2,
      spanGaps: false,
    };
    this.compareDatasetConfig = {
      label: 'Compared',
      xAxisID: 'x-axis-2',
      borderColor: this.compareLineColor,
      backgroundColor: compareGradient,
      pointHoverBackgroundColor: '#fff',
      showLine: true,
      borderWidth: 2,
      lineTension: 0.2,
      spanGaps: false,
    };
    this.currentXAxisConfig = {
      id: 'x-axis-1',
      type: 'category',
      ticks: {
        //display: this.isRetentionRate,
        display: false,
        fontColor: this.isRetentionRate ? '#743B91' : '',
        padding: 0,
        callback: (value, index, values) => {
          if (this.isRetentionRate) {
            return moment(value).format('MM/DD/YYYY');
          }
          return value;
        },
      },
      position: 'bottom',
      gridLines: {
        display: false,
      },
    };
    this.compareXAxisConfig = {
      id: 'x-axis-2',
      type: 'category',
      ticks: {
        //display: this.isRetentionRate,
        display: false,
        fontColor: this.isRetentionRate ? '#51A5D3' : '',
        padding: 0,
        callback: (value, index, values) => {
          if (this.isRetentionRate) {
            return moment(value).format('MM/DD/YYYY');
          }
          return value;
        },
      },
      gridLines: {
        display: false,
      },
    };
  },
  methods: {
    equalizeArrays(arr1, arr2) {
      const maxLength = Math.max(arr1.length, arr2.length);

      while (arr1.length < maxLength) {
        arr1.push({ date: '', value: null });
      }

      while (arr2.length < maxLength) {
        arr2.push({ date: '', value: null });
      }

      return [arr1, arr2];
    },
    renderLineChart: function () {
      // console.log('Input data:', this.data);
      this.datasets = [];
      this.xAxes = [];

      const { current, compare } = this.data || {};
      if (!current && !compare) {
        // console.warn('No valid data provided to render chart');
        return;
      }

      const getLabels = (arr) => arr.map((el) => el.date);
      const getRangeForTooltip = (arr) => arr.map((el) => el.range || { start: el.date, end: el.date });
      const getData = (arr) => arr.map((el) => {
        if (this.itemValueDecorator) {
          return this.itemValueDecorator(el.value, this.multiplier);
        }

        return el.value;
      });
      const formatDate = (date, format = 'L') => (date ? moment(date).format(format) : null);
      const getXValue = (xAxisID, tooltipItem) => {
        if (!this.xAxes[xAxisID] || !this.xAxes[xAxisID].labels) {
          // console.error(`Invalid xAxis configuration for ID ${xAxisID}`, this.xAxes);
          return '';
        }

        const dateRange = this.xAxes[xAxisID].dateRange;
        const range = dateRange && dateRange[tooltipItem.index] ? dateRange[tooltipItem.index] : null;
        const label = this.xAxes[xAxisID].labels[tooltipItem.index];

        if (!range || (!range.start && !range.end)) {
          // console.warn(`No valid range data for index ${tooltipItem.index} in xAxis ${xAxisID}`, range);
          return this.formatDate(label, 'L') || '';
        }

        if (range.start && range.end && range.start !== range.end) {
          return `${this.formatDate(range.start, 'L')} - ${this.formatDate(range.end, 'L')}`;
        }
        return this.formatDate(range.start || label, 'L') || '';
      };

      const updateAxisAndDataset = (data, datasetConfig, axisConfig) => {
        return {
          dataset: { data: getData(data), ...datasetConfig },
          axis: { labels: getLabels(data), dateRange: getRangeForTooltip(data), ...axisConfig },
        };
      };

      if (current) {
        if (compare) {
          [this.data.current, this.data.compare] = this.equalizeArrays(current, compare);
          const compareData = updateAxisAndDataset(
            compare,
            this.compareDatasetConfig,
            this.compareXAxisConfig,
          );
          this.datasets.push(compareData.dataset);
          this.xAxes.push(compareData.axis);
        }

        const currentData = updateAxisAndDataset(
          current,
          this.currentDatasetConfig,
          this.currentXAxisConfig,
        );
        this.datasets.push(currentData.dataset);
        this.xAxes.push(currentData.axis);
      }

      const xLabels = function () {
        const ctx = this.chart.ctx;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';

        const drawLabels = (xAxis, color, yOffset) => {
          if (!xAxis || !xAxis.options.labels) return;

          const firstLabel = xAxis.options.labels[0];
          const lastLabel = xAxis.options.labels[xAxis.options.labels.length - 1];

          const firstTick = firstLabel && moment(firstLabel).isValid() ? moment(firstLabel).format('MM/DD/YYYY') : '';
          const lastTick = lastLabel && moment(lastLabel).isValid() ? moment(lastLabel).format('MM/DD/YYYY') : '';

          ctx.fillStyle = color;
          if (firstTick) {
            ctx.fillText(firstTick, xAxis.left + 30, xAxis.bottom + yOffset);
          }
          if (lastTick) {
            ctx.fillText(lastTick, xAxis.right - 30, xAxis.bottom + yOffset);
          }
        };

        drawLabels(this.scales['x-axis-1'], '#743B91', 7);
        drawLabels(this.scales['x-axis-2'], '#51A5D3', 30);
      };

      this.renderChart(
        {
          datasets: this.datasets,
        },
        {
          tooltips: {
            mode: 'index',
            callbacks: {
              label: function (tooltipItem, data) {
                var xAxisID = tooltipItem.datasetIndex;
                if (!this.xAxes[xAxisID] || !this.xAxes[xAxisID].labels) return '';
                var xValue = getXValue(xAxisID, tooltipItem);
                var yValue = data.datasets[xAxisID].data[tooltipItem.index];
                return `${xValue}: ${
                  this.isCurrency
                    ? `$${this.formatAmount(yValue)}`
                    : yValue.toLocaleString()
                }`;
              }.bind(this),
            },
          },
          legend: {
            display: true,
            position: 'bottom',
            reverse: true,
            labels: {
              boxWidth: 10,
              padding: 30,
              usePointStyle: true,
            },
          },
          layout: {
            padding: {
              bottom: 0,
            },
          },
          scales: {
            xAxes: this.xAxes,
            yAxes: [
              {
                ticks: {
                  beginAtZero: true,
                  min: 0,
                  callback: (value) => {
                    return this.isCurrency ? `${this.formatAmount(value)}` : value;
                  },
                },
              },
            ],
          },
          plugins: [
            {
              afterDraw: xLabels,
            },
          ],
          animation: {
            onProgress: xLabels,
          },
          elements: {
            point: {
              radius: 0,
              hitRadius: 10,
              hoverRadius: 4,
              hoverBorderWidth: 3,
            },
          },
        },
      );
    },
  },
  mounted() {
    this.renderLineChart();
  },
};
</script>
